<script setup lang="ts">
import type { ToasterProps } from "./toast.types";
import { useToaster } from "./useToaster";

const props = withDefaults(defineProps<ToasterProps>(), {
  position: "bottom-right",
  expand: false,
  visibleToasts: 3,
  duration: 4000,
  closeButton: false,
  gap: 14,
  offset: "32px",
  richColors: false,
  dedup: true,
  dismissLabel: "Dismiss all",
});

const {
  classes,
  listClasses,
  toasts,
  isExpanded,
  handleToastEvent,
  handleMouseEnter,
  handleMouseLeave,
  handleDismissAll,
  toastStyle,
} = useToaster({ props });
</script>

<template>
  <div part="base" :class="classes" class="e-toaster" :style="{ padding: props.offset }">
    <div part="list" :class="listClasses" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
      <span
        v-if="isExpanded && toasts.length > 2"
        style="display: flex; justify-content: center; margin-bottom: 12px; order: -1"
        @pointerdown.stop
        @pointerup.stop="handleDismissAll"
      >
        <e-button size="xs" variant="outline">
          {{ props.dismissLabel }}
        </e-button>
      </span>
      <div style="position: relative; z-index: 0">
        <template v-for="(t, index) in toasts" :key="t.id">
          <e-toast
            :toast-id="t.id"
            :type="t.type"
            :color="t.color"
            :variant="t.variant"
            :title="t.title"
            :description="t.description"
            :duration="t.duration"
            :data-dismissible="JSON.stringify(t.dismissible ?? true)"
            :data-close-button="t.closeButton != null ? JSON.stringify(t.closeButton) : undefined"
            :data-rich-colors="JSON.stringify(t.richColors === true)"
            :data-count="t.count && t.count > 1 ? JSON.stringify(t.count) : undefined"
            :data-expanded="JSON.stringify(isExpanded)"
            :data-stacked="!isExpanded && index < toasts.length - 1 ? 'true' : undefined"
            :style="toastStyle(index)"
            @dismiss="handleToastEvent"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style>
:host {
  display: contents;
}
</style>
